
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentIcon from "@mui/icons-material/Assignment";
import UpdateIcon from "@mui/icons-material/Update";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	List,
	ListItem,
	Typography,
} from "@mui/material";
import dayjs from "dayjs";

import { useTime } from "@/components/Application/TimeContext";
import { calculateUpdateDuration } from "@/components/Archive/ActiveArchiveProcedures";
import { calculateDuration } from "@/components/Archive/Utils/helperFunctions";
import WaterfallChart from "@/components/Archive/WaterfallChart";

type Props = {
    open: boolean;
    handleClose: () => void;
    selectedItem: any;
};

const StepModal = (props: Props) => {
    const { timezone } = useTime();
    const { open, handleClose, selectedItem } = props;
	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
			<DialogTitle>{selectedItem.Name}</DialogTitle>
			<DialogContent>
				{/* Main info  */}
				<Box
					sx={{
						marginBottom: 3,
						padding: 2,
						backgroundColor: "#f0f4f8",
						borderRadius: "8px",
						display: "flex",
						flexDirection: "row",
						gap: 2, // Spacing between columns
						justifyContent: "space-between",
					}}
				>
					{/* Left Column */}
					<Box sx={{ flexBasis: "48%" }}>
						<Typography
							variant="body1"
							sx={{ fontWeight: "bold", fontSize: "1rem" }}
						>
							Description:
						</Typography>
						<Typography
							variant="body2"
							color="textSecondary"
							sx={{ marginBottom: 1 }}
						>
							{selectedItem.Description}
						</Typography>
						<Grid container alignItems="center" sx={{ marginBottom: 1 }}>
							<Grid item sx={{ display: "flex", alignItems: "center" }}>
								<AccessTimeIcon
									sx={{ fontSize: "1rem", color: "#555", marginRight: 0.5 }}
								/>
								<Typography
									variant="body2"
									sx={{ color: "#555", fontWeight: "550" }}
								>
									Created:
								</Typography>
							</Grid>
							<Grid item>
								<Typography
									variant="body2"
									sx={{ color: "#333", marginLeft: 1 }}
								>
									{dayjs
										.utc(selectedItem.Created)
										.tz(timezone)
										.format("DD/MM/YY HH:mm:ss")}
								</Typography>
							</Grid>
						</Grid>
						<Grid container alignItems="center">
							<Grid item sx={{ display: "flex", alignItems: "center" }}>
								<UpdateIcon
									sx={{ fontSize: "1rem", color: "#555", marginRight: 0.5 }}
								/>
								<Typography
									variant="body2"
									sx={{ color: "#555", fontWeight: "550" }}
								>
									Updated:
								</Typography>
							</Grid>
							<Grid item>
								<Typography
									variant="body2"
									sx={{ color: "#333", marginLeft: 1 }}
								>
									{dayjs
										.utc(selectedItem.Updated)
										.tz(timezone)
										.format("DD/MM/YY HH:mm:ss")}
								</Typography>
							</Grid>
						</Grid>
					</Box>

					{/* Right Column */}
					<Box sx={{ flexBasis: "48%", mt: 4 }}>
						<Grid container alignItems="center" sx={{ marginBottom: 1 }}>
							<Grid item sx={{ display: "flex", alignItems: "center" }}>
								<AssignmentIcon
									sx={{ fontSize: "1rem", color: "#555", marginRight: 0.5 }}
								/>
								<Typography
									variant="body2"
									sx={{ color: "#555", fontWeight: "550" }}
								>
									Project Type:
								</Typography>
							</Grid>
							<Grid item>
								<Typography
									variant="body2"
									sx={{ color: "#333", marginLeft: 1 }}
								>
									{selectedItem.ProjectType}
								</Typography>
							</Grid>
						</Grid>
						{selectedItem.Updates.length > 1 && (
							<Grid container alignItems="center" sx={{ marginTop: 1 }}>
								<Grid item sx={{ display: "flex", alignItems: "center" }}>
									<AccessTimeIcon
										sx={{
											fontSize: "1rem",
											color: "#555",
											marginRight: 0.5,
										}}
									/>
									<Typography
										variant="body2"
										sx={{ color: "#555", fontWeight: "550" }}
									>
										Total Duration:
									</Typography>
								</Grid>
								<Grid item>
									<Typography
										variant="body2"
										sx={{ color: "#333", marginLeft: 1 }}
									>
										{calculateDuration(selectedItem.Updates)}
									</Typography>
								</Grid>
							</Grid>
						)}
					</Box>
				</Box>
				<Box sx={{ m: 2 }}>
					<WaterfallChart updates={selectedItem.Updates} />
				</Box>
				{/* Updates */}
				<Box>
					<Typography variant="h6" sx={{ marginBottom: 2 }}>
						Updates
					</Typography>
					<List>
						{selectedItem.Updates.map((update: any, index: number) => (
							<ListItem
								key={index}
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "space-between",
									alignItems: "center",
									padding: 2,
									backgroundColor: "#f0f4f8",
									marginBottom: 1,
									borderRadius: "4px",
								}}
							>
								{/* Status Indicator */}
								<Box sx={{ display: "flex", alignItems: "center" }}>
									<Box
										sx={{
											width: 16,
											height: 16,
											borderRadius: "50%",
											backgroundColor: update.Status === 1 ? "green" : "red",
											marginRight: 2,
										}}
									/>
									<Typography variant="body2" sx={{ fontWeight: "550" }}>
										{update.User.UserFullName}
									</Typography>
								</Box>

								{/* Update Description */}
								<Box sx={{ flex: 1, marginLeft: 2 }}>
									<Typography variant="body2">
										{update.Status === 1 ? "Active" : "Inactive"}
									</Typography>
								</Box>

								{/* Start Time */}
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "flex-end",
									}}
								>
									<Typography variant="body2" sx={{ textAlign: "right" }}>
										Started at{" "}
										{dayjs
											.utc(update.Date)
											.tz(timezone)
											.format("DD/MM/YY HH:mm:ss")}
									</Typography>
									{index > 0 && (
										<Typography
											variant="body2"
											sx={{ fontStyle: "italic", textAlign: "right" }}
										>
											{calculateUpdateDuration(
												selectedItem.Updates[index - 1].Date,
												update.Date,
											)}
										</Typography>
									)}
								</Box>
							</ListItem>
						))}
					</List>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color="primary">
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default StepModal;
